import autobind from 'core-decorators/lib/autobind';
import { withBiEvent } from 'shared/bi/send-bi-event';
import { connect } from 'react-redux';

import { VODCSSModules, VODBiEvents } from 'shared/utils/wix-connect';
import { getNewRelic } from 'shared/analytics/new-relic';
import {
  getAllSettings,
  getSiteUrl,
  getRequestedWidgetSize,
  isPreviewHover,
  isVideoListPreviewHover,
  canShowChannelShare,
  canShowVideoList,
  canShowSignIn,
  canShowChannelInfo,
  isCompactPlayInFrame,
} from 'shared/selectors/app-settings';

import { isVisible as canShowSubscriptionButton } from 'widget/components/overlay-texts/channel/subscribe-button-text';

import { openFullScreenOverlay } from 'shared/utils/open-overlay';
import { isMobile, isTablet } from 'shared/utils/device/screen';
import { playerSize } from 'widget/utils/videos-sizes';

import { getMainVideoId } from 'widget/ui-selectors/selected-video-id';
import { canPlayFullOrPreview } from 'widget/ui-selectors/video-access';
import { isVideoPlayingOptimistic, isVideoPausedOptimistic } from 'widget/selectors/video-playback-status';
import { getVideosGroupedByIds } from 'shared/selectors/videos';
import { getCurrentSiteUser } from 'widget/selectors/current-site-user';
import { isActionBarVisible } from 'widget/views/desktop/compact/ui-selectors/ui-selectors';

import { DATA_KEY } from 'widget/data-components/paginated-channel-videos';
import { COMPACT_VIDEOS_IN_ROW, COMPACT_NUMBER_OF_ROWS } from 'shared/constants/compact-layout';
import STATS_EVENTS from 'shared/constants/stats/events';

import { selectVideo, resetSelectedVideo } from 'widget/redux/client/actions/select-video';
import { playVideo, pauseVideo, resetPlaybackStatus } from 'widget/redux/client/actions/change-playback-status';
import reportStats from 'widget/redux/server/actions/stats/report';
import { openPreviewPrompt } from 'widget/components/preview-prompt/preview-prompt';
import bi from 'shared/bi/bi';

import BaseView from 'widget/views/_base';

import UpdateWidgetSize from 'widget/data-components/update-widget-size';
import PaymentEvents from 'shared/components/payment-events/payment-events';
import ReloadStorage from 'widget/components/reload-storage/reload-storage';
import ResizeWidgetByContent from 'widget/views/desktop/compact/components/resize-widget-by-content/resize-widget-by-content';

import MobilePlayer from 'widget/components/players/mobile-player';
import DesktopPlayer from 'widget/components/players/desktop-player';

import Videos from 'widget/views/desktop/compact/components/videos/videos';
import ClosableOverlay from 'widget/views/desktop/compact/components/closable-overlay/closable-overlay';
import ShareChannel from 'widget/views/desktop/compact/components/share-channel/share-channel';
import { ChannelOverlay, VideoOverlay } from 'widget/views/desktop/compact/components/player-overlay/overlay';
import ChanelActions from 'widget/views/desktop/compact/components/channel-actions/channel-actions';
import ActionBar from 'widget/views/desktop/compact/components/action-bar/action-bar';
import Nav from 'widget/components/nav/nav';
import AnimateCrossfade from 'widget/components/animate-crossfade/animate-crossfade';

import InFrameActionCallbacks from './actions/overlay-action-callbacks/in-frame';
import InFrameMobileActionCallbacks from './actions/overlay-action-callbacks/in-frame-mobile';
import InOverlayActionCallbacks from './actions/overlay-action-callbacks/in-overlay';
import ChannelActionCallbacks from './actions/channel-action-callbacks';
import PlayerActionCallbacks from './actions/player-action-callbacks';

import Animate from 'widget/views/desktop/compact/components/animate/animate';

import styles from './compact.styl';
import ActionBarArrowRight from 'static/images/action-bar-arrow-right.svg';

const DEFAULT_STATE = {
  areAllVideosVisible: false,
  isShareOverlayVisible: false,
  isInFullScreen: false,
};

const IS_MOBILE_DEVICE = isMobile() || isTablet();
const MIN_WIDGET_WIDTH = 400;
const MAX_WIDGET_WIDTH = 980;

const ACTION_BAR_OPENED_HEIGHT = 60;
const ACTION_BAR_CLOSED_HEIGHT = 40;


function OverlayActionsCallbacks(store) {
  if (IS_MOBILE_DEVICE) {
    return InFrameMobileActionCallbacks;
  }

  if (isCompactPlayInFrame(store)) {
    return InFrameActionCallbacks;
  }

  return InOverlayActionCallbacks;
}


@connect((store, ownProps) => ({
  isVideoPaused: isVideoPausedOptimistic(store),
  isVideoPlaying: isVideoPlayingOptimistic(store),
  isPlayInFrame: isCompactPlayInFrame(store),
  mainVideoId: getMainVideoId(store, DATA_KEY),
  selectedVideoId: store.selectedVideoId,

  videoIdsByPageNumber: ownProps.paginatedChannelVideos.idChunks,
  currentVideosPageNumber: ownProps.paginatedChannelVideos.currentUIPage,
  goToPrevVideosPage: ownProps.paginatedChannelVideos.goBack,
  goToNextVideosPage: ownProps.paginatedChannelVideos.goForward,
  goToFirstVideosPage: ownProps.paginatedChannelVideos.toFirstPage,

  prevVideoId: ownProps.nextChannelVideos.prevId,
  nextVideoId: ownProps.nextChannelVideos.nextId,
  loadedVideoIds: ownProps.nextChannelVideos.loadedVideoIds,
  currentIdIndex: ownProps.nextChannelVideos.currentIdIndex,

  videoByIds: getVideosGroupedByIds(store),
  fetchVideos: ownProps.paginatedChannelVideos.fetch,
  isVideosFetching: ownProps.paginatedChannelVideos.isFetching,

  appSettings: getAllSettings(store),
  siteUrl: getSiteUrl(store),
  requestedWidgetSize: getRequestedWidgetSize(store),

  currentSiteUser: getCurrentSiteUser(store),
  canShowChannelCover: !store.isVideosTouched,
  canShowChannelInfo: canShowChannelInfo(store),
  canShowChannelShare: canShowChannelShare(store),
  canShowVideoList: canShowVideoList(store),
  canShowSignIn: canShowSignIn(store),
  canShowSubscriptionButton: canShowSubscriptionButton({ channel: ownProps.channelData }),
  isPreviewHover: isPreviewHover(store),
  isVideoListPreviewHover: isVideoListPreviewHover(store),
  isActionBarVisible: isActionBarVisible(store),

  OverlayActionsCallbacks: OverlayActionsCallbacks(store),
}), {
  selectVideo,
  playVideo,
  pauseVideo,
  resetSelectedVideo,
  resetPlaybackStatus,
  reportStats,
})
@VODBiEvents()
@VODCSSModules(styles)
export default class CompactView extends BaseView {
  static displayName = 'CompactView';

  static propTypes = {
    ...BaseView.propTypes,
    mainVideoId: React.PropTypes.string,
    channelVideoIds: React.PropTypes.arrayOf(React.PropTypes.string),

    prevPageVideosIds: React.PropTypes.arrayOf(React.PropTypes.string),
    nextPageVideosIds: React.PropTypes.arrayOf(React.PropTypes.string),
    loadedVideoIds: React.PropTypes.array,
    currentIdIndex: React.PropTypes.number,

    isActionBarVisible: React.PropTypes.bool,
    canShowChannelShare: React.PropTypes.bool,
    canShowChannelInfo: React.PropTypes.bool,
    canShowVideoList: React.PropTypes.bool,
    canShowSignIn: React.PropTypes.bool,
    isPreviewHover: React.PropTypes.bool,
    isVideoListPreviewHover: React.PropTypes.bool,

    OverlayActionsCallbacks: React.PropTypes.func,

    resetPlaybackStatus: React.PropTypes.func,
    resetSelectedVideo: React.PropTypes.func,
    reportStats: React.PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      ...DEFAULT_STATE,
      isInitialVideosLoaded: false,
    };

    getNewRelic().setCustomAttribute('layout', 'compact');
  }

  @autobind
  reset() {
    this.setState(DEFAULT_STATE);
    this.props.resetSelectedVideo();
    this.props.goToFirstVideosPage();
  }

  @autobind
  @withBiEvent('widget.actionBar.open.click')
  showAllVideos() {
    this.props.resetPlaybackStatus();
    this.setState({
      areAllVideosVisible: true,
    });
    bi.logWidgetSystem('videoList.show.requested');
  }

  @autobind
  hideAllVideos() {
    this.setState({
      areAllVideosVisible: false,
    });
  }

  @autobind
  toggleShareOverlay() {
    this.setState({
      isShareOverlayVisible: !this.state.isShareOverlayVisible,
    });
  }

  @autobind
  hideShareOverlay() {
    this.setState({
      isShareOverlayVisible: false,
    });
  }

  @autobind
  @withBiEvent('widget.share.clicked', function (networkName) {
    return {
      typeShare: networkName,
    };
  })
  reportChannelSharedStats(networkName) {
    const { reportStats, channelData } = this.props;

    reportStats(STATS_EVENTS.CHANNEL_SHARE, {
      network: {
        name: networkName,
      },
      channel: channelData,
    });
  }

  get shareButtons() {
    const { appSettings, channelData } = this.props;
    const { isShareOverlayVisible } = this.state;

    if (!isShareOverlayVisible) {
      return null;
    }

    return (
      <ShareChannel channelData={channelData}
                    appSettings={appSettings}
                    onShare={this.reportChannelSharedStats} />
    );
  }

  get shareChannelOverlay() {
    const { canShowChannelShare } = this.props;
    const { isShareOverlayVisible } = this.state;

    if (!canShowChannelShare) {
      return null;
    }

    return (
      <Animate styleName="share-channel-overlay"
               type="fade-in-out"
               when={isShareOverlayVisible}>
        <ClosableOverlay onClose={this.hideShareOverlay}>
          {this.shareButtons}
        </ClosableOverlay>
      </Animate>
    );
  }

  @autobind
  renderMainItemSlider() {
    const { currentIdIndex } = this.props;

    if (!this.mainVideo) {
      // TODO Move to component
      // each initial state need to be a single component
      return this.noVideosPlayerContent;
    }

    return (
      <AnimateCrossfade currentIdIndex={currentIdIndex > -1 ? currentIdIndex : 0}>
        {this.renderMainItemSliderContent()}
      </AnimateCrossfade>
    );
  }

  renderMainItemSliderNav() {
    const {
      canShowVideoList,
      isActionBarVisible,
      prevVideoId,
      nextVideoId,
    } = this.props;

    if (!canShowVideoList && isActionBarVisible) {
      return null;
    }

    return (
      <Nav prevButtonClassName={styles['nav-button-prev']}
           nextButtonClassName={styles['nav-button-next']}
           isPrevVisible={Boolean(prevVideoId)}
           isNextVisible={Boolean(nextVideoId)}
           onNextClick={this.selectNextVideo}
           onPrevClick={this.selectPrevVideo} />
    );
  }

  renderMainItemSliderContent() {
    const { loadedVideoIds, currentIdIndex, mainVideoId } = this.props;
    const videoIdsToRender = loadedVideoIds.length ? loadedVideoIds : [mainVideoId];
    const isCurrentOrPrevOrNext = index => Math.abs(currentIdIndex - index) <= 1;

    return _.map(videoIdsToRender, (videoId, index) => (
      isCurrentOrPrevOrNext(index) ?
        this.renderPlayerOverlay(videoId) :
        <div key={videoId} />
    ));
  }

  isVideoItemAtChannelCover(videoId) {
    return this.isMainItemChannelCover && videoId === this.mainVideo.id;
  }

  isAtChannelCover(videoId) {
    return this.canShowChannelCover && this.isVideoItemAtChannelCover(videoId);
  }

  @autobind
  renderPlayerOverlay(videoId) {
    const { width, height } = this.playerSize;
    const { channelData, OverlayActionsCallbacks, currentSiteUser, videoByIds } = this.props;
    const Overlay = this.isAtChannelCover(videoId) ? ChannelOverlay : VideoOverlay;
    const videoItem = videoByIds[videoId];

    if (!videoItem) {
      return null;
    }

    return (
      <div className={styles['video-overlay-container']}
           key={videoId}>
        <OverlayActionsCallbacks isMainVideo
                                 onBeforeClick={this.hideAllVideos}
                                 onBeforePlayRequested={this.hideAllVideos}
                                 channelId={channelData.id}
                                 videoId={videoItem.id}
                                 videoSource={videoItem.videoSource}>
          <Overlay currentSiteUser={currentSiteUser}
                   width={width}
                   height={height}
                   channelData={channelData}
                   videoItem={videoItem} />
        </OverlayActionsCallbacks>
      </div>
    );
  }

  get player() {
    const videoItem = this.mainVideo;

    if (!videoItem) {
      return null;
    }

    const { width, height } = this.playerContentSize;
    const { channelData, isPlayInFrame, isVideoPlaying, isVideoPaused, currentSiteUser } = this.props;

    const isPlayAllowed = canPlayFullOrPreview(channelData, videoItem, currentSiteUser);
    if (!isPlayAllowed) {
      return null;
    }

    const playerProps = {
      videoItem,
      channelData,
      width,
      height,
      openPreviewPrompt,
      onFullScreenChanged: this.setFullScreenState,
      paused: !isVideoPlaying,
    };

    const callbackProps = {
      channelId: channelData.id,
      videoId: videoItem.id,
    };

    if (IS_MOBILE_DEVICE) {
      return (
        <PlayerActionCallbacks {...callbackProps}>
          <MobilePlayer {...playerProps} />
        </PlayerActionCallbacks>
      );
    }
    //(isVideoPlaying || isVideoPaused) condition is only for performance reasons
    if (isPlayInFrame && (isVideoPlaying || isVideoPaused)) {
      return (
        <PlayerActionCallbacks {...callbackProps}>
          <DesktopPlayer {...playerProps} />
        </PlayerActionCallbacks>
      );
    }

    return null;
  }

  get playerContentSize() {
    const { canShowVideoList } = this.props;

    if (!canShowVideoList) {
      return this.playerSize;
    }

    const { width, height } = this.playerSize;
    return {
      width,
      height: height - (this.actionBarHeight - ACTION_BAR_CLOSED_HEIGHT),
    };
  }

  get playerSize() {
    const { requestedWidgetSize } = this.props;
    const width = Math.min(Math.max(requestedWidgetSize.width, MIN_WIDGET_WIDTH), MAX_WIDGET_WIDTH);
    return playerSize(width, 0);
  }

  get actionBarHeight() {
    return this.areActionsVisible ? ACTION_BAR_OPENED_HEIGHT : ACTION_BAR_CLOSED_HEIGHT;
  }

  get thumbnailSize() {
    const { width, height } = this.playerSize;

    return {
      width: width / COMPACT_VIDEOS_IN_ROW,
      height: (height - (ACTION_BAR_OPENED_HEIGHT - ACTION_BAR_CLOSED_HEIGHT)) / COMPACT_NUMBER_OF_ROWS,
    };
  }

  @autobind
  handleSlideToPrev() {
    bi.logWidgetSystem('videoList.changePage.requested');
    this.props.goToPrevVideosPage();
  }

  @autobind
  handleSlideToNext() {
    bi.logWidgetSystem('videoList.changePage.requested');
    this.props.goToNextVideosPage();
  }

  get videos() {
    const {
      channelData,
      videoIdsByPageNumber,
      currentVideosPageNumber,
      canShowVideoList,
      OverlayActionsCallbacks,
    } = this.props;

    if (!canShowVideoList) {
      return null;
    }

    return (
      <Videos containerWidth={this.playerContentSize.width}
              channelData={channelData}
              videoIdsByPageNumber={videoIdsByPageNumber}
              currentVideosPageNumber={currentVideosPageNumber}
              onSlideToPrev={this.handleSlideToPrev}
              onSlideToNext={this.handleSlideToNext}
              onBeforeClick={this.hideAllVideos}
              onBeforePlayRequested={this.hideAllVideos}
              OverlayActionsCallbacks={OverlayActionsCallbacks}
              thumbnailSize={this.thumbnailSize} />
    );
  }

  get actionBarNav() {
    const { canShowVideoList, nextVideoId, prevVideoId } = this.props;

    if (canShowVideoList) {
      return null;
    }

    return (
      <Nav nextButtonClassName={styles['action-bar-nav-button']}
           prevButtonClassName={styles['action-bar-nav-button']}
           isPrevVisible={Boolean(prevVideoId)}
           isNextVisible={Boolean(nextVideoId)}
           onNextClick={this.selectNextVideo}
           onPrevClick={this.selectPrevVideo}
           dataHook="actionBarNav"
           customArrowIcon={(
             <ActionBarArrowRight className={styles['action-bar-nav-icon']} />
           )} />
    );
  }

  @autobind
  handleActionBarContainerClick() {
    this.hideAllVideos();
    this.hideShareOverlay();
  }

  get actionBar() {
    const { isActionBarVisible } = this.props;

    if (!isActionBarVisible) {
      return null;
    }

    return (
      <ActionBar height={this.actionBarHeight}
                 areActionsVisible={this.areActionsVisible}
                 onActionsContainerClick={this.handleActionBarContainerClick}
                 onMenuClick={this.showAllVideos}
                 onButtonsClick={this.hideShareOverlay}>
        {this.actions}
        {this.actionBarNav}
      </ActionBar>
    );
  }

  @autobind
  handleShareRequest(clickEvent) {
    clickEvent.stopPropagation();
    this.toggleShareOverlay();
  }

  get actions() {
    const {
      channelData,
      canShowVideoList,
      canShowSignIn,
      canShowChannelShare,
      canShowChannelInfo,
      canShowSubscriptionButton,
    } = this.props;

    return (
      // TODO onUserLogin, onUserLogOut - etc. make BaseAccountButton dumb to only
      // call callbacks provided to it aka onUserLoginRequested...
      <ChannelActionCallbacks channelId={channelData.id}>
        <ChanelActions channelData={channelData}
                       canShowSignIn={canShowSignIn}
                       canShowChannelShare={canShowChannelShare}
                       canShowChannelInfo={canShowChannelInfo}
                       canShowCloseButton={canShowVideoList}
                       canShowSubscriptionButton={canShowSubscriptionButton}

                       onShareRequest={this.handleShareRequest}
                       onCloseRequest={this.hideAllVideos}
                       onLogInRequest={this.showLogInForm}
                       onLogOutRequest={this.logOut}
                       onAccountInfoAtOverlayRequest={this.openAccountInfoAtOverlay} />
      </ChannelActionCallbacks>
    );
  }

  get isVideoListVisible() {
    const { areAllVideosVisible } = this.state;
    const { isVideoListPreviewHover, isPreviewHover, canShowVideoList } = this.props;
    if (!canShowVideoList) {
      return false;
    }
    return areAllVideosVisible || isVideoListPreviewHover || isPreviewHover;
  }

  get areActionsVisible() {
    const { canShowVideoList } = this.props;
    return !canShowVideoList || this.isVideoListVisible;
  }

  @autobind()
  processDataFromStorage(data) {
    // TODO: get rid of this in view
    const { selectedVideoId, openOverlay, autoplay } = data;
    const { selectVideo, resetSelectedVideo, channelData, isPlayInFrame, playVideo } = this.props;

    if (!selectedVideoId) {
      resetSelectedVideo();
      return;
    }

    if ((isPlayInFrame || IS_MOBILE_DEVICE) && autoplay) {
      playVideo(selectedVideoId);
    } else {
      selectVideo(selectedVideoId);
    }

    if (!isPlayInFrame && openOverlay) {
      openFullScreenOverlay(`#/channel/${channelData.id}/video/${selectedVideoId}?autoplay=${autoplay}`);
    }
  }

  render() {
    if (!this.isUIBlockingDataLoaded) {
      return this.spinner;
    }

    const { isVideoPlaying, isVideoPaused } = this.props;

    return (
      <ResizeWidgetByContent>
        <section styleName="container"
                 ref={this.setContainerEl}
                 className="qa-widget-container">
          <UpdateWidgetSize disabled={this.state.isInFullScreen} />
          <ReloadStorage onReload={this.processDataFromStorage} />

          <div styleName="content"
               style={this.playerContentSize}>
            <Animate type="fade-in"
                     dataHook="videoList"
                     styleName="videos-in-animation"
                     when={this.isVideoListVisible}>
              {this.videos}
            </Animate>
            <Animate type="fade-in"
                     dataHook="playerContent"
                     when={!this.isVideoListVisible}>
              {this.player}
              {!isVideoPlaying && !isVideoPaused && this.renderMainItemSlider()}
              {!isVideoPlaying && !isVideoPaused && this.renderMainItemSliderNav()}
            </Animate>
            {this.shareChannelOverlay}
          </div>

          {this.actionBar}

          <PaymentEvents onRent={this.setCurrentVideoFromPayment}
                         onSale={this.setCurrentVideoFromPayment}
                         onSubscription={this.reset}
                         onSubscriptionCancel={this.reset} />
        </section>
      </ResizeWidgetByContent>
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/views/desktop/compact/compact.js